import React, { useMemo, useState } from "react";
import HorizontalBars from "./HorizontalBars";
import { ALL_SPORTS, buildProductRows, productsTable, sportChips } from "./courtStatsProducts";
import { fmtHours, fmtInt, fmtMoney, fmtMoneyCompact, fmtPct0 } from "./statsFormat";

/**
 * Por tipo de turno tab: which court "products" (sport + duration, e.g. "Pádel 90 min") bill the
 * most. One horizontal bar per product (single lime series, value at the tip, tooltip with
 * reservations, hours and share of the club's total), sport chips to narrow it down and a "Ver
 * tabla" twin with the same numbers. A response without `products` (older back) or without data
 * shows an empty state.
 *
 * @param {{current: object}} props  `current` is a stats response.
 */
export default function TurnosTab({ current }) {
  const [sport, setSport] = useState(ALL_SPORTS);
  const [view, setView] = useState("chart");

  const chips = useMemo(() => sportChips(current.products), [current]);
  // The chosen sport can vanish after a filter/range change: show everything instead of nothing.
  const activeSport = chips.some((chip) => chip.value === sport) ? sport : ALL_SPORTS;
  const rows = useMemo(() => buildProductRows(current.products, activeSport), [current, activeSport]);
  const table = useMemo(() => productsTable(rows), [rows]);

  const barRows = rows.map((row) => ({ key: row.key, label: row.label, value: row.revenue, valueText: fmtMoneyCompact(row.revenue) }));
  const tip = (bar) => {
    const row = rows.find((r) => r.key === bar.key);
    return {
      title: row.label,
      rows: [
        { value: fmtMoney(row.revenue), label: `facturado, ${fmtPct0(row.share)} del total` },
        { value: fmtInt(row.reservations), label: `reservas · ${fmtHours(row.hours)} reservadas` },
      ],
    };
  };

  return (
    <section className="stats-panel" aria-labelledby="stats-products-title">
      <div className="stats-panel__head">
        <div>
          <h2 className="stats-panel__title" id="stats-products-title">
            Turnos que más facturan
          </h2>
          <p className="stats-panel__sub">Ingresos cobrados por deporte y duración del turno, en el período.</p>
        </div>
        {chips.length > 1 && (
          <div className="stats-panel__tools">
            <div className="stats-seg stats-seg--wrap" role="group" aria-label="Deporte">
              {chips.map((chip) => (
                <button key={chip.value} type="button" aria-pressed={activeSport === chip.value} onClick={() => setSport(chip.value)}>
                  {chip.label}
                </button>
              ))}
            </div>
            <button type="button" className="stats-ghost-btn" aria-pressed={view === "table"} onClick={() => setView((v) => (v === "chart" ? "table" : "chart"))}>
              {view === "chart" ? "Ver tabla" : "Ver gráfico"}
            </button>
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="stats-footnote">Todavía no hay turnos cobrados en este período para mostrar.</p>
      ) : view === "chart" ? (
        <HorizontalBars rows={barRows} ariaLabel="Facturación por tipo de turno. El detalle está en la vista de tabla." tip={tip} />
      ) : (
        <div className="stats-table-wrap">
          <table>
            <thead>
              <tr>
                {table.headers.map((header, index) => (
                  <th key={header} className={index > 1 ? "num" : undefined}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((cells, rowIndex) => (
                <tr key={rows[rowIndex].key}>
                  {cells.map((cell, index) => (
                    <td key={index} className={index > 1 ? "num" : undefined}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
